import React, { useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import PropTypes from 'prop-types'
import { reverse } from 'named-urls'
import { useHistory } from 'react-router-dom'
import { Box, Button, Tooltip, Typography } from '@material-ui/core'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'
import { ConfirmationDialog, MenuButton } from 'components'

import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'

import { routes } from 'Routes'
import theme from 'theme'
import useStyles from './styles'

const ControlsTable = ({
  linkedDocuments,
  isLoading,
  documentId,
  readOnly = false,
  refresh = () => {},
}) => {
  const classes = useStyles()
  const history = useHistory()
  const snackbar = useSnackbar()

  const [pendingRemoveDocument, setPendingRemoveDocument] = useState(null)
  const [isRemoving, setIsRemoving] = useState(false)

  const { INACTIVE_STATUS_ID } = constants.governanceProject
  const { NOT_LINKABLE_STATUSES_IDS } = constants.governanceProjectActivity
  const { isProjectLocked } = helpers.governanceProject

  // Transição pelo react-router, e não `window.open`: a atividade é uma rota da própria aplicação, e
  // abrir em outra guia remontaria a aplicação inteira (bundle, providers, autenticação) do zero.
  //
  // O hash é a âncora da evidência: a atividade pode ter várias, e sem ele o usuário cairia no topo da
  // tela tendo que procurar qual delas originou o vínculo.
  const handleRedirectToActivity = (linkedDocument) =>
    history.push({
      pathname: reverse(routes.governanceProjects.activity.manage, {
        governanceProjectId: linkedDocument?.governanceProject?.id,
        activityId: linkedDocument?.governanceProjectActivity?.id,
      }),
      hash: `#${helpers.governanceProject.mountEvidenceAnchor(
        linkedDocument?.evidence?.id,
      )}`,
    })

  const handleCloseDialog = () => setPendingRemoveDocument(null)

  const getDisabledControlTooltip = (linkedDocument) => {
    const governanceProjectStatus = linkedDocument?.governanceProject?.status

    const isProjectDisabled =
      isProjectLocked(governanceProjectStatus) ||
      governanceProjectStatus === INACTIVE_STATUS_ID

    if (isProjectDisabled) {
      return 'Não é possível excluir controles de um projeto de governança concluído, arquivado ou inativo'
    }

    const isActivityLocked = NOT_LINKABLE_STATUSES_IDS.includes(
      linkedDocument?.governanceProjectActivity?.status,
    )

    if (isActivityLocked) {
      return 'Não é possível excluir controles de uma atividade concluída ou aguardando aprovação'
    }

    return ''
  }

  const handleConfirmRemove = async () => {
    try {
      setIsRemoving(true)

      await service.dponet.privacyPolicies.removeLinkedDocument({
        privacyPolicyId: documentId,
        linkedDocumentId: pendingRemoveDocument?.id,
      })

      snackbar.open({
        message: 'Controle removido com sucesso!',
        variant: 'success',
      })
      refresh()
      handleCloseDialog()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setIsRemoving(false)
    }
  }

  return (
    <Box width="100%">
      <PerfectScrollbar
        options={{ wheelPropagation: false, useBothWheelAxes: true }}
      >
        <Table
          emptyMessage="Nenhum vínculo encontrado"
          isLoading={isLoading}
          size="small"
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                width="5%"
                className={classes.initialContent}
              >
                Versão
              </TableCell>
              <TableCell
                align="left"
                width="20%"
                className={classes.initialContent}
              >
                Projeto de governança
              </TableCell>
              <TableCell align="left" width="20%">
                Questionário
              </TableCell>
              <TableCell
                align="left"
                width="25%"
                className={classes.finalContent}
              >
                Questão
              </TableCell>
              <TableCell
                align="left"
                width="25%"
                className={classes.finalContent}
              >
                Controle
              </TableCell>
              <TableCell
                align="center"
                width="5%"
                className={classes.finalContent}
              >
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linkedDocuments?.map((linkedDocument) => {
              const disabledTooltip = getDisabledControlTooltip(linkedDocument)

              return (
                <TableRow key={linkedDocument?.id}>
                  <TableCell
                    align="center"
                    width="5%"
                    className={classes.initialContent}
                  >
                    {linkedDocument?.documentVersion
                      ? `${linkedDocument.documentVersion.version}.${
                          linkedDocument.documentVersion.review || 0
                        }`
                      : '-'}
                  </TableCell>
                  <TableCell
                    align="left"
                    width="20%"
                    className={classes.initialContent}
                  >
                    {linkedDocument?.governanceProject?.name}
                  </TableCell>
                  <TableCell align="left" width="20%">
                    {linkedDocument?.governanceProjectQuestionnaire?.kind ||
                      '-'}
                  </TableCell>
                  <TableCell
                    align="left"
                    width="25%"
                    className={classes.finalContent}
                  >
                    {linkedDocument?.governanceProjectActivity?.question?.theme}
                  </TableCell>
                  <TableCell
                    align="left"
                    width="25%"
                    className={classes.finalContent}
                  >
                    {helpers.governanceProject.mountControlLabel(
                      linkedDocument?.evidence,
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    width="5%"
                    className={classes.finalContent}
                  >
                    <Box display="flex" justifyContent="center">
                      <MenuButton>
                        <Button
                          color="secondary"
                          fullWidth
                          size="small"
                          onClick={() =>
                            handleRedirectToActivity(linkedDocument)
                          }
                        >
                          Visualizar
                        </Button>
                        {!readOnly && (
                          <Tooltip title={disabledTooltip}>
                            <Box>
                              <Button
                                color="secondary"
                                fullWidth
                                size="small"
                                disabled={!!disabledTooltip}
                                onClick={() =>
                                  setPendingRemoveDocument(linkedDocument)
                                }
                              >
                                Excluir
                              </Button>
                            </Box>
                          </Tooltip>
                        )}
                      </MenuButton>
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </PerfectScrollbar>
      {!!pendingRemoveDocument && (
        <ConfirmationDialog
          open={!!pendingRemoveDocument}
          setOpen={handleCloseDialog}
          variant="error"
          accentColor={theme.palette.error.main}
          text={
            <Box textAlign="start">
              <Typography variant="h4" gutterBottom>
                Tem certeza de que deseja excluir este vínculo?
              </Typography>
              <Typography variant="body1">
                Essa ação não poderá ser desfeita!
              </Typography>
            </Box>
          }
          textButton="Excluir"
          textButtonCancel="Cancelar"
          actionAcceptButton={handleConfirmRemove}
          loading={isRemoving}
        />
      )}
    </Box>
  )
}

ControlsTable.propTypes = {
  linkedDocuments: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool.isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  readOnly: PropTypes.bool,
  refresh: PropTypes.func,
}

export default ControlsTable
